import { Alert, Drawer, Spin, Table, Tag } from 'antd'
import React, { useEffect, useState } from 'react'
import { getBilling } from './api'
import { COLORS, dateOnly, dateTime, eur, marginPct, stepLabel } from './format'
import { TERMS } from './Glossary'

export const formatPhone = (p) => {
  const s = String(p || '')
  const m = s.match(/^34(\d{3})(\d{2})(\d{2})(\d{2})$/)
  return m ? `+34 ${m[1]} ${m[2]} ${m[3]} ${m[4]}` : s
}

const TIER_TAG = {
  conversacion: { label: TERMS.conversacion.label, color: COLORS.conversacion },
  extra: { label: TERMS.extra.label, color: COLORS.extra },
  simple: { label: TERMS.simple.label, color: COLORS.simple },
  basico: { label: TERMS.basico.label, color: COLORS.basico },
  incluido_en_sesion: { label: 'Incluido', color: COLORS.incluido },
  no_enviado: { label: 'No aceptado', color: '#cbd5e1' }
}

const tierKey = (m) =>
  m.tier === 'conversacional' ? (m.sessionKind === 'extra' ? 'extra' : 'conversacion') : m.tier

const Box = ({ label, value, color, sub }) => (
  <div style={{ flex: '1 1 140px', border: '1px solid #e5e7eb', borderRadius: 10, padding: '8px 12px' }}>
    <div style={{ fontSize: 12, color: '#8c8c8c' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>}
  </div>
)

/**
 * Línea temporal de un teléfono: todos sus mensajes en las campañas del periodo, con la
 * tarifa de cada uno y el coste total de su conversación.
 */
const PhoneTimeline = ({ userId, phoneRow, campaigns, range, onClose }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!phoneRow) return undefined
    let active = true
    setLoading(true)
    setError(false)
    setData(null)
    const campaignIds = phoneRow.campaigns
      .map((i) => campaigns[i] && campaigns[i].campaignId)
      .filter(Boolean)
      .join(',')
    getBilling({ userId, phone: phoneRow.phone, campaignIds, from: range.from, to: range.to })
      .then((res) => active && setData(res))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [phoneRow, userId, campaigns, range])

  const t = data && data.totals
  const margin = t ? t.retailInRange - t.costInRange : 0

  const columns = [
    {
      title: 'Enviado',
      dataIndex: 'sent',
      render: (v) => dateTime(v),
      width: 130
    },
    {
      title: 'Campaña',
      dataIndex: 'campaignTimestamp',
      render: (v) => dateOnly(v),
      width: 100
    },
    {
      title: 'Paso del flujo',
      dataIndex: 'step',
      render: (v) => stepLabel(v)
    },
    {
      title: 'Tarifa',
      key: 'tier',
      render: (_, m) => {
        const tg = TIER_TAG[tierKey(m)] || { label: m.tier, color: '#cbd5e1' }
        return <Tag color={tg.color}>{tg.label}</Tag>
      }
    },
    {
      title: 'Coste',
      dataIndex: 'cost',
      align: 'right',
      render: (v, m) => (m.billable ? eur(v, 3) : '—')
    },
    {
      title: 'Entregado',
      dataIndex: 'delivered',
      align: 'center',
      render: (v, m) => (v ? '✓' : m.failed ? <span style={{ color: COLORS.fallido }}>Fallido</span> : '—')
    },
    {
      title: 'Visto',
      dataIndex: 'displayed',
      align: 'center',
      render: (v) => (v ? '✓' : '—')
    }
  ]

  return (
    <Drawer
      title={phoneRow ? `Teléfono ${formatPhone(phoneRow.phone)}` : ''}
      open={!!phoneRow}
      onClose={onClose}
      width="min(980px, calc(100vw - 40px))"
      destroyOnClose
    >
      <Spin spinning={loading} tip="Calculando la conversación…">
        {error && <Alert type="error" showIcon message="No se pudo cargar el detalle del teléfono" />}
        {t && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <Box
                label="Coste de su conversación (periodo)"
                value={eur(t.costInRange)}
                color="#b45309"
                sub={t.costAll !== t.costInRange ? `${eur(t.costAll)} contando todas las fechas` : null}
              />
              <Box label="Cobrado al cliente" value={eur(t.retailInRange)} />
              <Box
                label={`Margen (${marginPct(margin, t.retailInRange)})`}
                value={eur(margin)}
                color={margin >= 0 ? '#237804' : '#a8071a'}
              />
            </div>
            <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
              Se cobra: {t.conversacion} conversación · {t.extra} sesiones extra · {t.simple} Simple ·{' '}
              {t.basico} Básico · {t.bundled} mensajes incluidos gratis
            </div>
            <Table
              rowKey={(m) => m.msgId || `${m.campaignId}-${m.element}-${m.sent}`}
              size="small"
              columns={columns}
              dataSource={data.messages}
              pagination={false}
              scroll={{ x: 760 }}
              onRow={(m) => ({ style: m.inRange || !m.billable ? undefined : { opacity: 0.5 } })}
            />
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
              Filas atenuadas: se cobran fuera del periodo seleccionado. Cada mensaje después del
              inicial es una respuesta del bot a algo que hizo el usuario.
            </div>
          </>
        )}
      </Spin>
    </Drawer>
  )
}

export default PhoneTimeline
